import { useEffect, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { MegaphoneFill } from "react-bootstrap-icons";
import { cn } from "@utils";
import useStaffSearch from "./useStaffSearch";

const AVATAR_COLORS = [
  "bg-rose-500",
  "bg-orange-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-blue-500",
  "bg-indigo-500",
  "bg-violet-500",
  "bg-pink-500",
];

function initials(name = "") {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

// Deterministic tint so a colleague keeps the same avatar colour across the modal.
function colorFor(key = "") {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// The "Notify" tag editor beneath a note in edit mode: removable colleague chips plus an "Add"
// picker backed by the same staff search the composer's @-mention uses. Owns no note text — it only
// edits the tagged-users list, reported up via onChange as [{ id, name }]. The picker is a Headless
// UI Popover whose panel is anchored (portaled to <body>) so it escapes the chat scroll container
// rather than expanding the card and shifting the page.
export default function NoteTagEditor({ users, onChange, onLayoutChange, label = "Notify colleague" }) {
  const [query, setQuery] = useState("");
  const { results } = useStaffSearch(query, { sort: "alpha" });

  const taggedIds = new Set(users.map((u) => u.id));
  const available = results.filter((r) => !taggedIds.has(r.id));

  // Adding/removing a chip changes the card's height; let the thread re-pin to the bottom.
  useEffect(() => {
    onLayoutChange?.();
  }, [users.length, onLayoutChange]);

  const remove = (id) => onChange(users.filter((u) => u.id !== id));
  const add = (staff) => {
    onChange([...users, { id: staff.id, name: staff.name }]);
    setQuery("");
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <MegaphoneFill aria-hidden="true" className="size-3.5 shrink-0 text-zinc-400" />
      {users.map((user) => (
        <span
          key={user.id}
          className="inline-flex items-center gap-1 rounded-full bg-zinc-100 py-0.5 pl-2 pr-1 text-xs font-medium text-zinc-700"
        >
          @{user.name}
          <button
            type="button"
            onClick={() => remove(user.id)}
            aria-label={`Remove ${user.name}`}
            className="flex size-4 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-200 hover:text-zinc-600"
          >
            <XMarkIcon aria-hidden="true" className="size-3" />
          </button>
        </span>
      ))}

      <Popover>
        <PopoverButton className="inline-flex items-center gap-1 rounded-full border border-dashed border-zinc-300 px-2 py-0.5 text-xs font-medium text-zinc-500 hover:border-zinc-400 hover:text-zinc-700 focus:outline-none">
          + {label}
        </PopoverButton>
        <PopoverPanel
          focus
          anchor="bottom start"
          className="z-[60] w-72 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-lg [--anchor-gap:0.4rem]"
        >
          {({ close }) => (
            <>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search colleagues"
                className="w-full border-b border-zinc-100 px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
              />
              <div className="max-h-64 overflow-y-auto">
                {available.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-zinc-400">
                    {query.trim() ? "No matching colleagues" : "Everyone is already tagged"}
                  </div>
                ) : (
                  available.map((staff) => (
                    <button
                      key={staff.id}
                      type="button"
                      onClick={() => {
                        add(staff);
                        close();
                      }}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-zinc-50"
                    >
                      <span
                        className={cn(
                          "flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white",
                          colorFor(staff.id || staff.name),
                        )}
                      >
                        {initials(staff.name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-zinc-900">
                          {staff.name}
                        </span>
                        <span className="block truncate text-xs text-zinc-500">
                          {[staff.branch, staff.email].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                      {staff.role && (
                        <span className="ml-auto shrink-0 text-xs text-zinc-400">{staff.role}</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </PopoverPanel>
      </Popover>
    </div>
  );
}
